import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { PageShell } from '../components/PageShell';
import { StatusDot } from '../components/StatusDot';
import { EnvironmentPill } from '../components/EnvironmentPill';
import { colors, cardStyle } from '../theme';
import { listApplications, getApplicationEnv, getApplicationLogs } from '../api';
import { humanizeSlug } from '../nameUtils';
import { EditEnvironmentButton } from '../components/EditEnvironmentButton';
import { usePlatformPermissions } from '../usePlatformPermissions';
import type { ApplicationSummary } from '../types';

const TABS = ['Overview', 'Deployments', 'Infrastructure', 'Logs'] as const;

function InfrastructureTab({ applicationId }: { applicationId: string }) {
  const identityApi = useApi(identityApiRef);
  const [env, setEnv] = useState<Record<string, string> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getApplicationEnv(identityApi, applicationId)
      .then((result) => {
        if (!cancelled) setEnv(result);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  if (error) {
    return (
      <div role="alert" style={{ color: colors.danger, fontSize: 14 }}>
        Couldn&apos;t load environment variables: {error}
      </div>
    );
  }
  if (env === null) {
    return <div style={{ color: colors.textMuted, fontSize: 14 }}>Loading…</div>;
  }
  const entries = Object.entries(env);
  if (entries.length === 0) {
    return <div style={{ color: colors.textMuted, fontSize: 14 }}>No environment variables set on this app.</div>;
  }
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: colors.textMuted }}>
          Real values from Coolify — includes AWS resource identifiers (when this app requested
          S3/SQS/DynamoDB) and any custom variables set at create time.
        </span>
        <button
          type="button"
          onClick={() => setReveal((r) => !r)}
          style={{
            border: `1px solid ${colors.border}`,
            background: colors.surface,
            borderRadius: 6,
            padding: '4px 10px',
            fontSize: 12,
            fontWeight: 600,
            color: colors.text,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            marginLeft: 12,
          }}
        >
          {reveal ? 'Hide values' : 'Show values'}
        </button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            {entries.map(([key, value]) => (
              <tr key={key} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                <td style={{ padding: '8px 12px 8px 0', fontWeight: 600, color: colors.text, whiteSpace: 'nowrap' }}>
                  {key}
                </td>
                <td
                  style={{
                    padding: '8px 0',
                    color: colors.textMuted,
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                  }}
                >
                  {reveal ? value : '••••••••'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LogsTab({ applicationId }: { applicationId: string }) {
  const identityApi = useApi(identityApiRef);
  const [logs, setLogs] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout>>();
  const logRef = useRef<HTMLPreElement>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const result = await getApplicationLogs(identityApi, applicationId, 300);
        if (cancelled) return;
        setLogs(result.logs);
        setError(null);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
      if (!cancelled) pollTimer.current = setTimeout(tick, 4000);
    };
    tick();
    return () => {
      cancelled = true;
      clearTimeout(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logs]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: colors.textMuted }}>
          Live container logs — auto-refreshes every few seconds.
        </span>
      </div>
      {error && (
        <div role="alert" style={{ color: colors.danger, fontSize: 14, marginBottom: 12 }}>
          {error}
        </div>
      )}
      <pre
        ref={logRef}
        style={{
          background: '#0d1117',
          color: '#c9d1d9',
          borderRadius: 8,
          padding: 16,
          fontSize: 12,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          lineHeight: 1.5,
          maxHeight: 420,
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          margin: 0,
        }}
      >
        {logs === null ? 'Loading logs…' : logs || '(no log output yet)'}
      </pre>
    </div>
  );
}

export function ApplicationDetailsPage() {
  const { canEdit } = usePlatformPermissions();
  const { id } = useParams<{ id: string }>();
  const identityApi = useApi(identityApiRef);
  const [applications, setApplications] = useState<ApplicationSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<(typeof TABS)[number]>('Overview');

  useEffect(() => {
    let cancelled = false;
    listApplications(identityApi)
      .then((apps) => {
        if (!cancelled) setApplications(apps);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <PageShell>
        <div role="alert" style={{ color: colors.danger, fontSize: 14 }}>
          Couldn&apos;t load application: {error}
        </div>
      </PageShell>
    );
  }

  if (applications === null) {
    return (
      <PageShell>
        <div style={{ color: colors.textMuted, fontSize: 14 }}>Loading…</div>
      </PageShell>
    );
  }

  const app = applications.find((a) => a.id === id);

  if (!app) {
    return (
      <PageShell>
        <div style={{ color: colors.textMuted, fontSize: 14 }}>
          Application not found. <Link to="/applications">Back to My Applications</Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Link to="/applications" style={{ fontSize: 13, color: colors.textMuted, textDecoration: 'none' }}>
        ← My Applications
      </Link>

      <div style={{ ...cardStyle, padding: 24, marginTop: 16, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: colors.text, margin: 0 }}>{app.name}</h1>
            <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>{app.type}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {canEdit && <EditEnvironmentButton app={app} onDone={() => window.location.reload()} />}
            <EnvironmentPill environment={app.environment} />
            <StatusDot status={app.status} />
          </div>
        </div>
        {app.url && (
          <div style={{ marginTop: 12 }}>
            <a href={app.url} target="_blank" rel="noreferrer" style={{ color: colors.primary, fontSize: 14 }}>
              {app.url}
            </a>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${colors.border}`, marginBottom: 20 }}>
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            aria-current={tab === t}
            style={{
              border: 'none',
              background: 'transparent',
              padding: '10px 16px',
              fontSize: 14,
              fontWeight: tab === t ? 600 : 400,
              color: tab === t ? colors.primary : colors.textMuted,
              borderBottom: tab === t ? `2px solid ${colors.primary}` : '2px solid transparent',
              cursor: 'pointer',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      <div style={{ ...cardStyle, padding: 24 }}>
        {tab === 'Overview' && (
          <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', rowGap: 12, columnGap: 16 }}>
            <dt style={{ color: colors.textMuted, fontSize: 13 }}>Owner</dt>
            <dd style={{ margin: 0, fontSize: 14 }}>{app.owner}</dd>
            <dt style={{ color: colors.textMuted, fontSize: 13 }}>Department</dt>
            <dd style={{ margin: 0, fontSize: 14 }}>
              {app.department ? humanizeSlug(app.department) : <span style={{ color: colors.textMuted }}>Unknown</span>}
            </dd>
            <dt style={{ color: colors.textMuted, fontSize: 13 }}>Repository</dt>
            <dd style={{ margin: 0, fontSize: 14 }}>
              {app.repoFullName ? (
                <a
                  href={`https://github.com/${app.repoFullName}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: colors.primary }}
                >
                  {app.repoFullName}
                </a>
              ) : (
                <span style={{ color: colors.textMuted }}>Unknown (deployed before source tracking)</span>
              )}
            </dd>
            <dt style={{ color: colors.textMuted, fontSize: 13 }}>Branch</dt>
            <dd style={{ margin: 0, fontSize: 14 }}>
              {app.branch ?? <span style={{ color: colors.textMuted }}>Unknown</span>}
            </dd>
            <dt style={{ color: colors.textMuted, fontSize: 13 }}>Environment</dt>
            <dd style={{ margin: 0, fontSize: 14 }}>{app.environment}</dd>
            <dt style={{ color: colors.textMuted, fontSize: 13 }}>Status</dt>
            <dd style={{ margin: 0, fontSize: 14 }}>
              <StatusDot status={app.status} />
            </dd>
            <dt style={{ color: colors.textMuted, fontSize: 13 }}>Last updated</dt>
            <dd style={{ margin: 0, fontSize: 14 }}>{new Date(app.updatedAt).toLocaleString()}</dd>
          </dl>
        )}
        {tab === 'Deployments' && (
          <p style={{ margin: 0, fontSize: 14, color: colors.textMuted }}>
            Deployment history for this application is not yet exposed by the Platform Controller API.
          </p>
        )}
        {tab === 'Infrastructure' && <InfrastructureTab applicationId={app.id} />}
        {tab === 'Logs' && <LogsTab applicationId={app.id} />}
      </div>
    </PageShell>
  );
}
